/**
 * Admin Universities Management Tab Component
 * With Commission and Intake Management
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api, { universitiesAPI, dbToolsAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { 
  Plus, Building, MapPin, Loader2, Trash2, FileSpreadsheet, 
  Pencil, Percent, Calendar, CheckCircle, XCircle, DollarSign, Filter, GraduationCap, RefreshCw,
  Sparkles, Upload, X, Globe
} from 'lucide-react';

// Available intake options - Updated with 2026 intakes
const INTAKE_OPTIONS = [
  // 2025 intakes
  'January 2025', 'February 2025', 'March 2025', 'April 2025', 'May 2025',
  'June 2025', 'July 2025', 'August 2025', 'September 2025', 'October 2025',
  'November 2025', 'December 2025',
  // 2026 intakes
  'January 2026', 'February 2026', 'March 2026', 'April 2026', 'May 2026',
  'June 2026', 'July 2026', 'August 2026', 'September 2026', 'October 2026',
  'November 2026', 'December 2026',
  // 2027 intakes
  'January 2027', 'February 2027', 'March 2027', 'April 2027', 'May 2027',
  'June 2027', 'July 2027', 'August 2027', 'September 2027'
];

export const UniversitiesTab = ({ universities, onRefresh, onShowCSVImport, user }) => {
  const [showAddUniversity, setShowAddUniversity] = useState(false);
  const [showEditUniversity, setShowEditUniversity] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState(null);
  const [deletingUniversityId, setDeletingUniversityId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedForBulk, setSelectedForBulk] = useState([]);
  const [showBulkCommission, setShowBulkCommission] = useState(false);
  const [bulkCommission, setBulkCommission] = useState({ commission: '', agentShare: '70' });
  const [countryFilter, setCountryFilter] = useState('all');
  const [normalizing, setNormalizing] = useState(false);
  // AI Pakistan-requirements bulk enrichment
  const [pakEnrichStatus, setPakEnrichStatus] = useState(null);  // { status, total, done, failed, current_uni }
  const pakPollRef = useRef(null);
  const [seedingCourses, setSeedingCourses] = useState(false);

  // Fast, no-AI import of the bundled France/NZ/Turkey course CSVs.
  // Independent of the slow GPT-5.2 enrichment — useful when you just need
  // to get the courses into the system quickly.
  const handleSeedBundledCourses = async () => {
    if (!window.confirm(
      'Import the bundled France · New Zealand · Turkey course CSVs?\n\n' +
      'Idempotent — already-loaded rows are skipped. Finishes in a few seconds. No AI.'
    )) return;
    setSeedingCourses(true);
    try {
      const res = await universitiesAPI.seedBundledCourses();
      const d = res.data || {};
      toast.success(
        `Done — added ${d.universities_created || 0} universit${d.universities_created === 1 ? 'y' : 'ies'}, ${d.courses_created || 0} courses`
      );
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import courses');
    } finally {
      setSeedingCourses(false);
    }
  };
  // AI commission-sheet uploader
  const [showCommissionPdfDialog, setShowCommissionPdfDialog] = useState(false);
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfSyncing, setPdfSyncing] = useState(false);
  const [parsedCommissions, setParsedCommissions] = useState(null); // { intake_label, country, entries[] }

  // Handle normalize countries (fix Irish universities incorrectly labeled as UK)
  const handleNormalizeCountries = async () => {
    if (!window.confirm('This will:\n1. Normalize UK variants (UK, U.K.) to "United Kingdom"\n2. Fix Irish universities incorrectly labeled as UK\n\nProceed?')) return;
    
    setNormalizing(true);
    try {
      const res = await dbToolsAPI.normalizeCountries();
      const dd = res.data.deduped || {};
      const merged = dd.duplicate_groups_merged || 0;
      const deleted = dd.rows_deleted || 0;
      toast.success(
        `Fixed ${res.data.irish_fixed?.universities || 0} Irish · ` +
        `Normalized ${res.data.uk_normalized?.universities || 0} UK · ` +
        `Merged ${merged} duplicate group${merged === 1 ? '' : 's'} (deleted ${deleted} dup rows)`
      );
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to normalize countries');
    } finally {
      setNormalizing(false);
    }
  };

  // ---------------------------------------------------------------------------
  // AI Pakistan-Requirements enrichment — kicks off a backend job and polls
  // for progress every 2 s. Idempotent: only universities missing
  // `pakistan_requirements` are processed unless `force` is set.
  // ---------------------------------------------------------------------------
  const fetchPakEnrichStatus = async () => {
    try {
      const res = await universitiesAPI.getPakistanEnrichmentStatus();
      setPakEnrichStatus(res.data || null);
      // Stop polling once the job is no longer running/seeding
      const inProgress = res.data?.status === 'running' || res.data?.status === 'seeding';
      if (res.data?.status && !inProgress && pakPollRef.current) {
        clearInterval(pakPollRef.current);
        pakPollRef.current = null;
        if (res.data.status === 'completed') {
          toast.success(`Enrichment complete · updated ${(res.data.done || 0) - (res.data.failed || 0)} universities`);
          onRefresh();
        } else if (res.data.status === 'error') {
          toast.error(`Enrichment failed: ${res.data.error || 'unknown error'}`);
        }
      }
    } catch (error) {
      // 401/403 — silently stop polling
      if (pakPollRef.current) {
        clearInterval(pakPollRef.current);
        pakPollRef.current = null;
      }
    }
  };

  const handleStartPakEnrichment = async (force = false) => {
    const missing = universities.filter((u) => !u.pakistan_requirements).length;
    const target = force ? universities.length : missing;
    if (!window.confirm(
      `Run AI enrichment on ${target || 'all'} universit${target === 1 ? 'y' : 'ies'} missing Pakistan requirements?\n\n` +
      `This will also seed the bundled France / New Zealand / Turkey course CSVs (idempotent — skipped if already loaded).\n\n` +
      `Calls GPT-5.2 for each row to extract Pakistani-specific entry criteria. Safe to re-run.`
    )) return;
    try {
      // seed=true ensures missing France/NZ/Turkey data is loaded before enrichment
      await universitiesAPI.startPakistanEnrichment(force, true);
      toast.success('Job started — seeding then enriching');
      // Start polling
      fetchPakEnrichStatus();
      if (pakPollRef.current) clearInterval(pakPollRef.current);
      pakPollRef.current = setInterval(fetchPakEnrichStatus, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start enrichment');
    }
  };

  // On mount, fetch current status (resume polling if a job is in progress)
  useEffect(() => {
    fetchPakEnrichStatus();
    return () => {
      if (pakPollRef.current) {
        clearInterval(pakPollRef.current);
        pakPollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (pakEnrichStatus?.status === 'running' && !pakPollRef.current) {
      pakPollRef.current = setInterval(fetchPakEnrichStatus, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pakEnrichStatus?.status]);

  // Normalize country names (UK/Britain/England/Scotland/Wales -> United Kingdom)
  const normalizeCountry = (country) => {
    if (!country) return country;
    const normalized = country.trim();
    const lc = normalized.toLowerCase();
    if (['uk', 'u.k.', 'u.k', 'britain', 'great britain', 'england', 'scotland', 'wales', 'northern ireland', 'gb'].includes(lc)) {
      return 'United Kingdom';
    }
    return normalized;
  };

  // Get unique countries from universities (normalized, no duplicates)
  const uniqueCountries = useMemo(() => {
    const countriesSet = new Set();
    universities.forEach(u => {
      if (u.country) {
        countriesSet.add(normalizeCountry(u.country));
      }
    });
    return [...countriesSet].sort();
  }, [universities]);
  
  // Text search state (name / city / country / ID)
  const [uniSearchQuery, setUniSearchQuery] = useState('');

  // Filter universities by country + text search
  const filteredUniversities = useMemo(() => {
    let list = universities;
    if (countryFilter !== 'all') {
      list = list.filter(u => normalizeCountry(u.country) === countryFilter);
    }
    const q = uniSearchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.city || '').toLowerCase().includes(q) ||
        (u.country || '').toLowerCase().includes(q) ||
        (u.id || '').toLowerCase().includes(q) ||
        (u.website || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [universities, countryFilter, uniSearchQuery]);
  
  const [newUniversity, setNewUniversity] = useState({
    name: '',
    country: 'United Kingdom',
    city: '',
    website: '',
    ranking: '',
    commission_percentage: '',
    agent_share_percentage: '70',
    available_intakes: [],
    is_accepting_applications: true
  });

  const handleAddUniversity = async (e) => {
    e.preventDefault();
    try {
      await universitiesAPI.create({
        name: newUniversity.name,
        country: newUniversity.country,
        city: newUniversity.city,
        website: newUniversity.website || null,
        ranking: newUniversity.ranking ? parseInt(newUniversity.ranking) : null,
        commission_percentage: newUniversity.commission_percentage ? parseFloat(newUniversity.commission_percentage) : null,
        agent_share_percentage: parseFloat(newUniversity.agent_share_percentage) || 70,
        available_intakes: newUniversity.available_intakes.length > 0 ? newUniversity.available_intakes : null,
        is_accepting_applications: newUniversity.is_accepting_applications
      });
      toast.success('University added successfully');
      setShowAddUniversity(false);
      setNewUniversity({ 
        name: '', country: 'United Kingdom', city: '', website: '', ranking: '',
        commission_percentage: '', agent_share_percentage: '70',
        available_intakes: [], is_accepting_applications: true
      });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add university');
    }
  };

  const handleEditUniversity = (uni) => {
    setEditingUniversity({
      ...uni,
      commission_percentage: uni.commission_percentage || '',
      agent_share_percentage: uni.agent_share_percentage || 70,
      available_intakes: uni.available_intakes || [],
      is_accepting_applications: uni.is_accepting_applications !== false
    });
    setShowEditUniversity(true);
  };

  const handleSaveUniversity = async () => {
    if (!editingUniversity) return;
    setSaving(true);
    try {
      await universitiesAPI.update(editingUniversity.id, {
        name: editingUniversity.name,
        country: editingUniversity.country,
        city: editingUniversity.city,
        website: editingUniversity.website,
        ranking: editingUniversity.ranking ? parseInt(editingUniversity.ranking) : null,
        commission_percentage: editingUniversity.commission_percentage ? parseFloat(editingUniversity.commission_percentage) : null,
        agent_share_percentage: parseFloat(editingUniversity.agent_share_percentage) || 70,
        available_intakes: editingUniversity.available_intakes?.length > 0 ? editingUniversity.available_intakes : null,
        is_accepting_applications: editingUniversity.is_accepting_applications,
        // Admission criteria
        min_ielts: editingUniversity.min_ielts ? parseFloat(editingUniversity.min_ielts) : null,
        min_gpa: editingUniversity.min_gpa ? parseFloat(editingUniversity.min_gpa) : null,
        english_requirements: editingUniversity.english_requirements || null,
        academic_requirements: editingUniversity.academic_requirements || null,
        additional_requirements: editingUniversity.additional_requirements || null
      });
      toast.success('University updated successfully');
      setShowEditUniversity(false);
      setEditingUniversity(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update university');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUniversity = async (uniId) => {
    if (!window.confirm('Are you sure? This will delete all courses associated with this university.')) return;
    setDeletingUniversityId(uniId);
    try {
      await universitiesAPI.delete(uniId);
      toast.success('University and associated courses deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete university');
    } finally {
      setDeletingUniversityId(null);
    }
  };

  const handleBulkCommissionUpdate = async () => {
    if (selectedForBulk.length === 0) {
      toast.error('Please select universities first');
      return;
    }
    if (!bulkCommission.commission) {
      toast.error('Please enter commission percentage');
      return;
    }
    setSaving(true);
    try {
      await universitiesAPI.setBulkCommission(
        selectedForBulk,
        parseFloat(bulkCommission.commission),
        parseFloat(bulkCommission.agentShare) || 70
      );
      toast.success(`Commission updated for ${selectedForBulk.length} universities`);
      setShowBulkCommission(false);
      setSelectedForBulk([]);
      setBulkCommission({ commission: '', agentShare: '70' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update commissions');
    } finally {
      setSaving(false);
    }
  };

  const toggleBulkSelection = (uniId) => {
    setSelectedForBulk(prev => 
      prev.includes(uniId) 
        ? prev.filter(id => id !== uniId)
        : [...prev, uniId]
    );
  };

  const selectAllForBulk = () => {
    if (selectedForBulk.length === universities.length) {
      setSelectedForBulk([]);
    } else {
      setSelectedForBulk(universities.map(u => u.id));
    }
  };

  const toggleIntake = (intake, isEditing = false) => {
    if (isEditing) {
      const current = editingUniversity?.available_intakes || [];
      setEditingUniversity({
        ...editingUniversity,
        available_intakes: current.includes(intake)
          ? current.filter(i => i !== intake)
          : [...current, intake]
      });
    } else {
      const current = newUniversity.available_intakes || [];
      setNewUniversity({
        ...newUniversity,
        available_intakes: current.includes(intake)
          ? current.filter(i => i !== intake)
          : [...current, intake]
      });
    }
  };

  // Check if user can edit (super admin or has permission)
  const canEdit = user?.is_super_admin || user?.permissions?.can_manage_universities;

  return (
    <div className="space-y-6">
      {/* Add University & CSV Import Section */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Manage Universities</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Add universities, set commissions, and manage intakes</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {user?.is_super_admin && (
              <Button 
                variant="outline" 
                onClick={handleNormalizeCountries} 
                disabled={normalizing}
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                data-testid="normalize-countries-btn"
              >
                {normalizing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Fix Countries & Dedupe
              </Button>
            )}
            {user?.is_super_admin && (
              <Button
                variant="outline"
                onClick={handleSeedBundledCourses}
                disabled={seedingCourses}
                className="text-teal-700 border-teal-300 hover:bg-teal-50"
                data-testid="seed-bundled-courses-btn"
                title="Quickly import France / New Zealand / Turkey courses & universities. No AI — finishes in a few seconds."
              >
                {seedingCourses ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Globe className="w-4 h-4 mr-2" />}
                Import France/NZ/Turkey
              </Button>
            )}
            {user?.is_super_admin && (
              <Button
                variant="outline"
                onClick={() => handleStartPakEnrichment(false)}
                disabled={pakEnrichStatus?.status === 'running' || pakEnrichStatus?.status === 'seeding'}
                className="text-purple-700 border-purple-300 hover:bg-purple-50"
                data-testid="enrich-pakistan-btn"
                title="Seeds bundled France/NZ/Turkey CSVs (if missing) + runs GPT-5.2 to extract Pakistani-applicant entry requirements"
              >
                {pakEnrichStatus?.status === 'seeding'
                  ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  : pakEnrichStatus?.status === 'running'
                    ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    : <Sparkles className="w-4 h-4 mr-2" />}
                {pakEnrichStatus?.status === 'seeding'
                  ? 'Seeding courses…'
                  : pakEnrichStatus?.status === 'running'
                    ? `Enriching… ${pakEnrichStatus.done || 0}/${pakEnrichStatus.total || 0}`
                    : 'Seed + Enrich Universities'}
              </Button>
            )}
            {selectedForBulk.length > 0 && user?.is_super_admin && (
              <Dialog open={showBulkCommission} onOpenChange={setShowBulkCommission}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-600 text-white hover:bg-orange-700" data-testid="bulk-commission-btn">
                    <Percent className="w-4 h-4 mr-2" />
                    Set Commission ({selectedForBulk.length})
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white dark:bg-slate-800">
                  <DialogHeader>
                    <DialogTitle>Set Bulk Commission</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Setting commission for {selectedForBulk.length} universities
                    </p>
                    <div className="space-y-2">
                      <Label>Commission Percentage (%)</Label>
                      <Input
                        type="number"
                        value={bulkCommission.commission}
                        onChange={(e) => setBulkCommission({ ...bulkCommission, commission: e.target.value })}
                        placeholder="e.g., 15"
                        min="0"
                        max="100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Agent Share (%)</Label>
                      <Input
                        type="number"
                        value={bulkCommission.agentShare}
                        onChange={(e) => setBulkCommission({ ...bulkCommission, agentShare: e.target.value })}
                        placeholder="70"
                        min="0"
                        max="100"
                      />
                      <p className="text-xs text-gray-500">H E Direct gets {100 - (parseFloat(bulkCommission.agentShare) || 70)}%</p>
                    </div>
                    <Button onClick={handleBulkCommissionUpdate} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Update Commission
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            <Dialog open={showAddUniversity} onOpenChange={setShowAddUniversity}>
              <DialogTrigger asChild>
                <Button className="bg-teal-600 text-white hover:bg-teal-700 font-semibold" data-testid="add-university-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add University
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white dark:bg-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-gray-800 dark:text-white">Add New University</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddUniversity} className="space-y-4">
                  <div className="space-y-2">
                    <Label>University Name *</Label>
                    <Input
                      value={newUniversity.name}
                      onChange={(e) => setNewUniversity({ ...newUniversity, name: e.target.value })}
                      placeholder="e.g., University of London"
                      required
                      data-testid="uni-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Country *</Label>
                      <Select 
                        value={newUniversity.country} 
                        onValueChange={(val) => setNewUniversity({ ...newUniversity, country: val })}
                      >
                        <SelectTrigger data-testid="uni-country">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                          <SelectItem value="USA">USA</SelectItem>
                          <SelectItem value="Canada">Canada</SelectItem>
                          <SelectItem value="Ireland">Ireland</SelectItem>
                          <SelectItem value="Germany">Germany</SelectItem>
                          <SelectItem value="France">France</SelectItem>
                          <SelectItem value="Netherlands">Netherlands</SelectItem>
                          <SelectItem value="New Zealand">New Zealand</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>City *</Label>
                      <Input
                        value={newUniversity.city}
                        onChange={(e) => setNewUniversity({ ...newUniversity, city: e.target.value })}
                        placeholder="e.g., London"
                        required
                        data-testid="uni-city"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Website</Label>
                      <Input
                        value={newUniversity.website}
                        onChange={(e) => setNewUniversity({ ...newUniversity, website: e.target.value })}
                        placeholder="https://..."
                        data-testid="uni-website"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>World Ranking</Label>
                      <Input
                        type="number"
                        value={newUniversity.ranking}
                        onChange={(e) => setNewUniversity({ ...newUniversity, ranking: e.target.value })}
                        placeholder="e.g., 50"
                        data-testid="uni-ranking"
                      />
                    </div>
                  </div>
                  
                  {/* Commission Section */}
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" /> Commission Settings
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Commission %</Label>
                        <Input
                          type="number"
                          value={newUniversity.commission_percentage}
                          onChange={(e) => setNewUniversity({ ...newUniversity, commission_percentage: e.target.value })}
                          placeholder="e.g., 15"
                          min="0"
                          max="100"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Agent Share %</Label>
                        <Input
                          type="number"
                          value={newUniversity.agent_share_percentage}
                          onChange={(e) => setNewUniversity({ ...newUniversity, agent_share_percentage: e.target.value })}
                          placeholder="70"
                          min="0"
                          max="100"
                        />
                        <p className="text-xs text-gray-500">H E Direct: {100 - (parseFloat(newUniversity.agent_share_percentage) || 70)}%</p>
                      </div>
                    </div>
                  </div>

                  {/* Intake Section */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Available Intakes
                    </h4>
                    <div className="flex items-center gap-2 mb-3">
                      <Checkbox
                        checked={newUniversity.is_accepting_applications}
                        onCheckedChange={(checked) => setNewUniversity({ ...newUniversity, is_accepting_applications: checked })}
                      />
                      <span className="text-sm">Currently accepting applications</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                      {INTAKE_OPTIONS.map(intake => (
                        <label key={intake} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={newUniversity.available_intakes?.includes(intake)}
                            onCheckedChange={() => toggleIntake(intake, false)}
                          />
                          {intake}
                        </label>
                      ))}
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold" data-testid="uni-submit">
                    Add University
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <Button
              onClick={onShowCSVImport}
              variant="outline"
              className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
              data-testid="import-csv-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            {user?.is_super_admin && (
              <Button
                onClick={() => setShowCommissionPdfDialog(true)}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30"
                data-testid="upload-commission-pdf-btn"
                title="Upload a partner commission PDF — AI extracts every university & rate, you review, then sync"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Upload Commission Sheet (AI)
              </Button>
            )}
          </div>
        </div>

        {/* Pakistan-Requirements enrichment progress bar */}
        {(pakEnrichStatus?.status === 'running' || pakEnrichStatus?.status === 'seeding') && (
          <div className="mt-3 p-3 rounded-lg border border-purple-200 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800" data-testid="enrich-pakistan-progress">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-medium text-purple-800 dark:text-purple-200">
                <Sparkles className="w-3.5 h-3.5 inline mr-1" />
                {pakEnrichStatus.status === 'seeding'
                  ? 'Seeding bundled course CSVs (France · NZ · Turkey)…'
                  : 'AI enriching Pakistan requirements'}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-purple-700 dark:text-purple-300 font-mono">
                  {pakEnrichStatus.status === 'seeding'
                    ? '…'
                    : <>
                        {pakEnrichStatus.done || 0} / {pakEnrichStatus.total || 0}
                        {pakEnrichStatus.failed > 0 && <span className="text-rose-600 ml-2">· {pakEnrichStatus.failed} failed</span>}
                      </>}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm('Abort the running enrichment job? Progress so far will be kept; you can restart it any time.')) return;
                    try {
                      await universitiesAPI.resetPakistanEnrichment();
                      if (pakPollRef.current) { clearInterval(pakPollRef.current); pakPollRef.current = null; }
                      toast.success('Job aborted');
                      fetchPakEnrichStatus();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to abort');
                    }
                  }}
                  className="text-xs text-rose-600 hover:text-rose-700 underline underline-offset-2"
                  data-testid="enrich-pakistan-abort"
                >
                  Abort
                </button>
              </div>
            </div>
            <div className="h-2 rounded-full bg-purple-200 dark:bg-purple-900/50 overflow-hidden">
              <div
                className="h-full bg-purple-600 dark:bg-purple-400 transition-all"
                style={{
                  width: pakEnrichStatus.status === 'seeding'
                    ? '8%'
                    : pakEnrichStatus.total
                      ? `${Math.min(100, ((pakEnrichStatus.done || 0) / pakEnrichStatus.total) * 100)}%`
                      : '0%',
                }}
              />
            </div>
            {pakEnrichStatus.current_uni && (
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-1.5 italic truncate">
                Now: {pakEnrichStatus.current_uni}
              </p>
            )}
          </div>
        )}
        {pakEnrichStatus?.status === 'completed' && pakEnrichStatus.finished_at && (
          <div className="mt-3 p-2.5 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 text-sm text-green-800 dark:text-green-200 flex items-center gap-2" data-testid="enrich-pakistan-success">
            <CheckCircle className="w-4 h-4" />
            Last enrichment finished {new Date(pakEnrichStatus.finished_at).toLocaleString()}
            {pakEnrichStatus.failed > 0
              ? <span className="text-rose-600 ml-2">· {pakEnrichStatus.failed} failed</span>
              : <span className="text-green-700 ml-2">· all rows updated</span>}
          </div>
        )}
        
        {/* Filter & Bulk Selection Controls */}
        {universities.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-4 border-t border-gray-200 dark:border-slate-700">
            {/* Text search */}
            <div className="relative flex-1 sm:flex-initial">
              <input
                type="text"
                value={uniSearchQuery}
                onChange={(e) => setUniSearchQuery(e.target.value)}
                placeholder="Search universities by name, city, country, ID..."
                className="w-full sm:w-80 pl-3 pr-8 py-2 text-sm bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-md text-gray-800 dark:text-white"
                data-testid="university-search-input"
              />
              {uniSearchQuery && (
                <button
                  onClick={() => setUniSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Country Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger className="w-[200px] bg-white dark:bg-slate-700 text-left" data-testid="country-filter">
                  <SelectValue placeholder="Filter by Country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries ({universities.length})</SelectItem>
                  {uniqueCountries.map(country => (
                    <SelectItem key={country} value={country} className="text-left">
                      {country} ({universities.filter(u => normalizeCountry(u.country) === country).length})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Export CSV */}
            <button
              onClick={async () => {
                try {
                  const { reportsAPI, downloadCSV } = await import('@/lib/api');
                  await downloadCSV(reportsAPI.exportUniversities(), `universities_report_${new Date().toISOString().slice(0,10)}.csv`);
                  toast.success('Universities CSV downloaded');
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Export failed');
                }
              }}
              className="px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"
              data-testid="universities-export-csv"
            >
              📥 Export CSV
            </button>
            
            {/* Select All - Super Admin Only */}
            {user?.is_super_admin && (
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={selectedForBulk.length === filteredUniversities.length && filteredUniversities.length > 0}
                    onCheckedChange={() => {
                      if (selectedForBulk.length === filteredUniversities.length) {
                        setSelectedForBulk([]);
                      } else {
                        setSelectedForBulk(filteredUniversities.map(u => u.id));
                      }
                    }}
                  />
                  Select All {countryFilter !== 'all' ? `(${countryFilter})` : ''}
                </label>
                {selectedForBulk.length > 0 && (
                  <span className="text-sm text-gray-500">{selectedForBulk.length} selected</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Universities List */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-6">
          Partner Universities ({filteredUniversities.length})
          {countryFilter !== 'all' && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              - Filtered by {countryFilter}
            </span>
          )}
        </h3>
        {filteredUniversities.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No universities found {countryFilter !== 'all' ? `in ${countryFilter}` : ''}
          </div>
        ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUniversities.map((uni) => (
            <div 
              key={uni.id} 
              className={`bg-gray-50 dark:bg-slate-700 rounded-xl p-5 border transition-colors ${
                selectedForBulk.includes(uni.id) 
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' 
                  : 'border-transparent hover:border-teal-300'
              }`}
              data-testid={`admin-uni-${uni.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {user?.is_super_admin && (
                    <Checkbox
                      checked={selectedForBulk.includes(uni.id)}
                      onCheckedChange={() => toggleBulkSelection(uni.id)}
                      className="mt-1 flex-shrink-0"
                    />
                  )}
                  <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                    <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h4 className="text-gray-800 dark:text-white font-medium mb-1 truncate" title={uni.name}>{uni.name}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{uni.city}, {uni.country}</span>
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {uni.ranking && (
                        <Badge className="bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300 text-xs">
                          Rank #{uni.ranking}
                        </Badge>
                      )}
                      {uni.commission_percentage && (
                        <Badge className="bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 text-xs" title={uni.commission_notes || undefined}>
                          {uni.commission_percentage}% comm
                        </Badge>
                      )}
                      {uni.commission_type === 'fixed_gbp' && uni.commission_amount_gbp && (
                        <Badge className="bg-rose-100 dark:bg-rose-900 text-rose-700 dark:text-rose-300 text-xs" title={uni.commission_notes || undefined}>
                          £{uni.commission_amount_gbp.toLocaleString()} fixed
                        </Badge>
                      )}
                      {uni.commission_intake && (
                        <Badge className="bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300 text-xs">
                          {uni.commission_intake}
                        </Badge>
                      )}
                      {uni.is_accepting_applications === false ? (
                        <Badge className="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 text-xs">
                          <XCircle className="w-3 h-3 mr-1" /> Closed
                        </Badge>
                      ) : uni.available_intakes?.length > 0 && (
                        <Badge className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" /> {uni.available_intakes.length} intakes
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  {(user?.is_super_admin || user?.permissions?.can_manage_universities) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditUniversity(uni)}
                      className="text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 h-8 w-8 p-0"
                      data-testid={`edit-uni-${uni.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  {user?.is_super_admin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteUniversity(uni.id)}
                      disabled={deletingUniversityId === uni.id}
                      className="text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 h-8 w-8 p-0"
                      data-testid={`delete-uni-${uni.id}`}
                    >
                      {deletingUniversityId === uni.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Edit University Dialog */}
      <Dialog open={showEditUniversity} onOpenChange={setShowEditUniversity}>
        <DialogContent className="bg-white dark:bg-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Edit University</DialogTitle>
          </DialogHeader>
          {editingUniversity && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>University Name *</Label>
                <Input
                  value={editingUniversity.name}
                  onChange={(e) => setEditingUniversity({ ...editingUniversity, name: e.target.value })}
                  placeholder="e.g., University of London"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country *</Label>
                  <Select 
                    value={editingUniversity.country} 
                    onValueChange={(val) => setEditingUniversity({ ...editingUniversity, country: val })}
                  >
                    <SelectTrigger data-testid="edit-uni-country">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                      <SelectItem value="USA">USA</SelectItem>
                      <SelectItem value="Canada">Canada</SelectItem>
                      <SelectItem value="Ireland">Ireland</SelectItem>
                      <SelectItem value="Germany">Germany</SelectItem>
                      <SelectItem value="France">France</SelectItem>
                      <SelectItem value="Netherlands">Netherlands</SelectItem>
                      <SelectItem value="New Zealand">New Zealand</SelectItem>
                      <SelectItem value="Australia">Australia</SelectItem>
                      <SelectItem value="Switzerland">Switzerland</SelectItem>
                      <SelectItem value="Italy">Italy</SelectItem>
                      <SelectItem value="Spain">Spain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={editingUniversity.city}
                    onChange={(e) => setEditingUniversity({ ...editingUniversity, city: e.target.value })}
                    placeholder="e.g., London"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={editingUniversity.website || ''}
                    onChange={(e) => setEditingUniversity({ ...editingUniversity, website: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>World Ranking</Label>
                  <Input
                    type="number"
                    value={editingUniversity.ranking || ''}
                    onChange={(e) => setEditingUniversity({ ...editingUniversity, ranking: e.target.value })}
                    placeholder="e.g., 50"
                  />
                </div>
              </div>
              
              {/* Commission Section */}
              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Commission Settings (70/30 Split)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Commission % from University</Label>
                    <Input
                      type="number"
                      value={editingUniversity.commission_percentage || ''}
                      onChange={(e) => setEditingUniversity({ ...editingUniversity, commission_percentage: e.target.value })}
                      placeholder="e.g., 15"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Agent Share %</Label>
                    <Input
                      type="number"
                      value={editingUniversity.agent_share_percentage || 70}
                      onChange={(e) => setEditingUniversity({ ...editingUniversity, agent_share_percentage: e.target.value })}
                      placeholder="70"
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-gray-500">H E Direct gets: {100 - (parseFloat(editingUniversity.agent_share_percentage) || 70)}%</p>
                  </div>
                </div>
                {editingUniversity.commission_percentage && (
                  <div className="mt-3 p-3 bg-white dark:bg-slate-700 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      <strong>Commission Split Preview:</strong><br />
                      University pays: {editingUniversity.commission_percentage}% commission<br />
                      → Agent receives: {((parseFloat(editingUniversity.commission_percentage) * (parseFloat(editingUniversity.agent_share_percentage) || 70)) / 100).toFixed(1)}%<br />
                      → H E Direct receives: {((parseFloat(editingUniversity.commission_percentage) * (100 - (parseFloat(editingUniversity.agent_share_percentage) || 70))) / 100).toFixed(1)}%
                    </p>
                  </div>
                )}
              </div>

              {/* Intake Section */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Available Intakes
                </h4>
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    checked={editingUniversity.is_accepting_applications}
                    onCheckedChange={(checked) => setEditingUniversity({ ...editingUniversity, is_accepting_applications: checked })}
                  />
                  <span className="text-sm">Currently accepting applications</span>
                </div>
                <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                  {INTAKE_OPTIONS.map(intake => (
                    <label key={intake} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={editingUniversity.available_intakes?.includes(intake)}
                        onCheckedChange={() => toggleIntake(intake, true)}
                      />
                      {intake}
                    </label>
                  ))}
                </div>
              </div>

              {/* Admission Criteria Section */}
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-3 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" /> Admission Criteria
                </h4>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="space-y-2">
                    <Label>Minimum IELTS Score</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={editingUniversity.min_ielts || ''}
                      onChange={(e) => setEditingUniversity({ ...editingUniversity, min_ielts: e.target.value })}
                      placeholder="e.g., 6.0"
                      min="0"
                      max="9"
                      data-testid="edit-min-ielts"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Minimum GPA (out of 4.0)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingUniversity.min_gpa || ''}
                      onChange={(e) => setEditingUniversity({ ...editingUniversity, min_gpa: e.target.value })}
                      placeholder="e.g., 2.5"
                      min="0"
                      max="4"
                      data-testid="edit-min-gpa"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>English Requirements</Label>
                    <Textarea
                      value={editingUniversity.english_requirements || ''}
                      onChange={(e) => setEditingUniversity({ ...editingUniversity, english_requirements: e.target.value })}
                      placeholder="e.g., IELTS 6.5 overall with no band less than 6.0, TOEFL 90..."
                      className="min-h-[60px]"
                      data-testid="edit-english-requirements"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Academic Requirements</Label>
                    <Textarea
                      value={editingUniversity.academic_requirements || ''}
                      onChange={(e) => setEditingUniversity({ ...editingUniversity, academic_requirements: e.target.value })}
                      placeholder="e.g., Bachelor's degree with 2:2 or equivalent, relevant work experience..."
                      className="min-h-[60px]"
                      data-testid="edit-academic-requirements"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Additional Requirements</Label>
                    <Textarea
                      value={editingUniversity.additional_requirements || ''}
                      onChange={(e) => setEditingUniversity({ ...editingUniversity, additional_requirements: e.target.value })}
                      placeholder="e.g., Portfolio required for art courses, Interview may be required..."
                      className="min-h-[60px]"
                      data-testid="edit-additional-requirements"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveUniversity} disabled={saving} className="w-full bg-teal-600 text-white hover:bg-teal-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Commission PDF Uploader Dialog */}
      <Dialog open={showCommissionPdfDialog} onOpenChange={(open) => { setShowCommissionPdfDialog(open); if (!open) setParsedCommissions(null); }}>
        <DialogContent className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="commission-pdf-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Upload Commission Sheet (AI-extracted)
            </DialogTitle>
          </DialogHeader>

          {!parsedCommissions ? (
            <div className="space-y-4 py-2">
              <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 text-sm text-amber-800 dark:text-amber-200">
                <p className="font-semibold mb-1">How it works</p>
                <ol className="list-decimal list-inside space-y-0.5 text-xs">
                  <li>Upload the partner's PDF rate sheet</li>
                  <li>GPT-5.2 extracts every university, rate (% or fixed £), and notes</li>
                  <li>You review the result; nothing is saved until you click Confirm</li>
                  <li>Confirm to upsert into the universities table (idempotent on name)</li>
                </ol>
              </div>

              <label className="block">
                <div className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${pdfParsing ? 'border-amber-400 bg-amber-50/40 dark:bg-amber-900/10' : 'border-gray-300 dark:border-slate-600 hover:border-amber-400'}`}>
                  {pdfParsing ? (
                    <>
                      <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">AI is reading your PDF… (30–60s)</p>
                      <p className="text-xs text-gray-500 mt-1">Extracting universities, rates and notes via GPT-5.2</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Click to upload a PDF rate sheet</p>
                      <p className="text-xs text-gray-500 mt-1">Max 10MB · One intake per sheet</p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  disabled={pdfParsing}
                  data-testid="commission-pdf-file-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!file.name.toLowerCase().endsWith('.pdf')) {
                      toast.error('Please upload a PDF file');
                      return;
                    }
                    setPdfParsing(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      const { data } = await api.post('/admin/universities/parse-commission-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
                      setParsedCommissions(data);
                      toast.success(`AI extracted ${data.entry_count} universities — please review`);
                    } catch (err) {
                      toast.error(getErrorMessage(err, 'Failed to parse PDF'));
                    } finally {
                      setPdfParsing(false);
                      e.target.value = '';
                    }
                  }}
                />
              </label>
            </div>
          ) : (
            <div className="space-y-4 py-2" data-testid="commission-pdf-review">
              {/* Header summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 border border-emerald-200 dark:border-emerald-700 rounded-xl p-3">
                  <p className="text-xs text-emerald-700 dark:text-emerald-300 uppercase font-semibold tracking-wide">Intake</p>
                  <p className="text-sm font-bold text-emerald-900 dark:text-emerald-100 truncate">{parsedCommissions.intake_label}</p>
                </div>
                <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/30 dark:to-blue-900/30 border border-sky-200 dark:border-sky-700 rounded-xl p-3">
                  <p className="text-xs text-sky-700 dark:text-sky-300 uppercase font-semibold tracking-wide">Country</p>
                  <p className="text-sm font-bold text-sky-900 dark:text-sky-100 truncate">{parsedCommissions.country}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 border border-amber-200 dark:border-amber-700 rounded-xl p-3">
                  <p className="text-xs text-amber-700 dark:text-amber-300 uppercase font-semibold tracking-wide">Universities</p>
                  <p className="text-sm font-bold text-amber-900 dark:text-amber-100">{parsedCommissions.entry_count}</p>
                </div>
              </div>

              {/* Editable table */}
              <div className="border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
                <div className="grid grid-cols-[1fr_70px_90px_1fr_30px] gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-900/40 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <div>University</div><div>Type</div><div>Rate</div><div>Notes</div><div></div>
                </div>
                <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-100 dark:divide-slate-700">
                  {parsedCommissions.entries.map((e, i) => (
                    <div key={i} className="grid grid-cols-[1fr_70px_90px_1fr_30px] gap-2 px-3 py-2 text-xs items-center" data-testid={`pdf-row-${i}`}>
                      <div className="font-medium text-gray-800 dark:text-gray-100 truncate" title={e.name}>{e.name}</div>
                      <div>
                        <Badge className={e.type === 'fixed_gbp' ? 'bg-rose-100 text-rose-700 border-0 text-[10px]' : 'bg-orange-100 text-orange-700 border-0 text-[10px]'}>
                          {e.type === 'fixed_gbp' ? '£' : '%'}
                        </Badge>
                      </div>
                      <div className="font-semibold text-gray-800 dark:text-gray-100">
                        {e.type === 'fixed_gbp' ? `£${(e.amount_gbp || 0).toLocaleString()}` : `${e.percent}%`}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 truncate" title={e.notes || ''}>{e.notes || '—'}</div>
                      <button
                        onClick={() => setParsedCommissions({ ...parsedCommissions, entries: parsedCommissions.entries.filter((_, idx) => idx !== i), entry_count: parsedCommissions.entry_count - 1 })}
                        className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded p-1 transition-colors"
                        title="Remove this row"
                        data-testid={`pdf-row-remove-${i}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                💡 Review the AI extraction above. Wrong row? Click ✕ to drop it before syncing. Notes / rates can be edited later via the University detail page.
              </p>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setParsedCommissions(null)}
                  disabled={pdfSyncing}
                  data-testid="pdf-reupload-btn"
                >
                  Re-upload a different PDF
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                  disabled={pdfSyncing || parsedCommissions.entries.length === 0}
                  data-testid="pdf-confirm-sync-btn"
                  onClick={async () => {
                    setPdfSyncing(true);
                    try {
                      const { data } = await api.post('/admin/universities/sync-parsed-commissions', {
                        intake_label: parsedCommissions.intake_label,
                        country: parsedCommissions.country,
                        entries: parsedCommissions.entries,
                      });
                      toast.success(data?.message || 'Synced');
                      setShowCommissionPdfDialog(false);
                      setParsedCommissions(null);
                      if (onRefresh) onRefresh();
                    } catch (err) {
                      toast.error(getErrorMessage(err, 'Sync failed'));
                    } finally {
                      setPdfSyncing(false);
                    }
                  }}
                >
                  {pdfSyncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing…</> : <><CheckCircle className="w-4 h-4 mr-2" />Confirm & sync {parsedCommissions.entries.length} universities</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UniversitiesTab;
